import { memo, useCallback } from 'react';

import {
  useTrackToggledAnonymousAccountRequirementOff,
  useTrackToggledAnonymousAccountRequirementOn,
} from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';

import { OnOffShareToggleRow } from '~/components/ShareModal/components/OnOffShareToggleRow';
import { ExpirationDateRow } from '~/components/ShareModal/components/SecurityPrivacySection/ExpirationDateRow';
import { PasscodeProtectionRow } from '~/components/ShareModal/components/SecurityPrivacySection/PasscodeProtectionRow';
import { ShareModalSectionLabel } from '~/components/ShareModal/components/ShareModalSectionLabel';
import { ToggleInfoTooltip } from '~/components/ShareModal/components/ToggleInfoTooltip';
import { UpsellFlashIconButton } from '~/components/ShareModal/components/UpsellFlashIconButton';
import { type OnShortUrlUpdate } from '~/components/ShareModal/types/onShortUrlUpdate';
import { type ShareModalUpdateResponse } from '~/components/ShareModal/types/shareModalUpdateResponse';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';

const TRACKABLE_OBJECT_TYPES = ['asset', 'clip', 'board', 'canvas'] as const;
type TrackableObjectType = (typeof TRACKABLE_OBJECT_TYPES)[number];

const isTrackableObjectType = (objectType: string): objectType is TrackableObjectType =>
  (TRACKABLE_OBJECT_TYPES as readonly string[]).includes(objectType);

export type SecurityPrivacySectionProps = {
  shortIdInfo: ShortUrlResponse;
  isLoading: boolean;
  settingsDisabled?: boolean;
  onShortUrlUpdate: OnShortUrlUpdate<ShareModalUpdateResponse>;
};
export const SecurityPrivacySection = memo(
  ({ shortIdInfo, isLoading, settingsDisabled = false, onShortUrlUpdate }: SecurityPrivacySectionProps) => {
    const { requireAccountEnabled } = useShareSettings();
    const { trackToggledAnonymousAccountRequirementOn } = useTrackToggledAnonymousAccountRequirementOn();
    const { trackToggledAnonymousAccountRequirementOff } = useTrackToggledAnonymousAccountRequirementOff();

    const onCheckedChange = useCallback(
      async (checked: boolean) => {
        const response = await onShortUrlUpdate({
          props: { requireAccount: checked },
        });

        // Analytics events support asset/clip/board/canvas — skip collection responses.
        if (!isTrackableObjectType(response.objectType)) {
          return;
        }

        if (checked) {
          trackToggledAnonymousAccountRequirementOn({
            share_url: response.id,
            object_id: response.objectId,
            object_type: response.objectType,
          });
        } else {
          trackToggledAnonymousAccountRequirementOff({
            share_url: response.id,
            object_id: response.objectId,
            object_type: response.objectType,
          });
        }
      },
      [onShortUrlUpdate, trackToggledAnonymousAccountRequirementOff, trackToggledAnonymousAccountRequirementOn],
    );

    const handleChangePasscode = useCallback(
      (passcode: string | null) => onShortUrlUpdate({ props: { passcode } }),
      [onShortUrlUpdate],
    );

    const handleChangeExpirationDate = useCallback(
      (expirationDate: string | null) => onShortUrlUpdate({ props: { expirationDate } }),
      [onShortUrlUpdate],
    );

    return (
      <div className="flex flex-col gap-2">
        <ShareModalSectionLabel title="Security & Privacy" />
        <OnOffShareToggleRow
          label="Require name and email to view"
          labelAccessory={
            !requireAccountEnabled ? (
              <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>
            ) : (
              <ToggleInfoTooltip text="Link visitors must enter name and email to view" />
            )
          }
          isLoading={isLoading}
          checked={!!shortIdInfo.requireAccount}
          disabled={settingsDisabled || !requireAccountEnabled}
          onToggle={onCheckedChange}
        />
        <PasscodeProtectionRow
          shortIdInfo={shortIdInfo}
          onChangePasscode={handleChangePasscode}
          settingsDisabled={settingsDisabled}
        />
        <ExpirationDateRow
          shortIdInfo={shortIdInfo}
          onChangeExpirationDate={handleChangeExpirationDate}
          settingsDisabled={settingsDisabled}
        />
      </div>
    );
  },
);

SecurityPrivacySection.displayName = 'SecurityPrivacySection';
